// Journal d'audit — GET /admin/journal · POST /admin/journal/archiver
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, ShieldAlert, CheckCircle2, Archive } from 'lucide-react';
import { EmptyState } from '../ui/UIComponents';
import { getJournal, archiverJournal } from '../../services/adminJournal';
import { authService } from '../../services/authService';

function isSuperAdmin() {
  const u = authService.getCurrentUser();
  if (!u) return false;
  const n = u.niveau;
  return n === 1 || n === '1' || (typeof n === 'string' && n.toUpperCase() === 'SUPER_ADMIN');
}

function errMsg(err) {
  if (err?.status === 401) return 'Session expirée. Veuillez vous reconnecter.';
  if (err?.status === 403) return 'Vous n’avez pas l’autorisation de consulter le journal d’activité.';
  return err?.message || 'Impossible de charger le journal d’activité.';
}

function formatJsonField(v) {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'string') {
    try {
      const p = JSON.parse(v);
      return JSON.stringify(p, null, 0);
    } catch {
      return v;
    }
  }
  return String(v);
}

function extractEntries(res) {
  if (!res) return [];
  if (Array.isArray(res.entrees)) return res.entrees;
  if (Array.isArray(res.journal)) return res.journal;
  if (Array.isArray(res.entries)) return res.entries;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res)) return res;
  return [];
}

export default function JournalTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const [limite, setLimite] = useState(50);
  const [decalage, setDecalage] = useState(0);
  const [niveauGravite, setNiveauGravite] = useState('');
  const [action, setAction] = useState('');
  const [cibleType, setCibleType] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limite, decalage };
      if (niveauGravite) params.niveau_gravite = niveauGravite;
      if (action) params.action = action;
      if (cibleType) params.cible_type = cibleType;
      const res = await getJournal(params);
      setRows(extractEntries(res));
    } catch (err) {
      setRows([]);
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  }, [limite, decalage, niveauGravite, action, cibleType]);

  useEffect(() => {
    load();
  }, [load]);

  const runArchive = async () => {
    setConfirmArchive(false);
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await archiverJournal();
      setSuccess('Archivage du journal lancé.');
      await load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex gap-2 items-center">
          <ShieldAlert size={14} /> {error}
        </div>
      )}
      {success && (
        <div className="p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex gap-2 items-center">
          <CheckCircle2 size={14} /> {success}
        </div>
      )}

      <div className="dsi-card p-4 flex flex-col lg:flex-row gap-3 flex-wrap">
        <label className="text-xs space-y-1">
          <span className="font-semibold text-slate-600">Limite</span>
          <input
            type="number"
            min={1}
            max={200}
            className="dsi-input w-24"
            value={limite}
            onChange={(e) => setLimite(Number(e.target.value) || 50)}
          />
        </label>
        <label className="text-xs space-y-1">
          <span className="font-semibold text-slate-600">À partir de</span>
          <input
            type="number"
            min={0}
            className="dsi-input w-24"
            value={decalage}
            onChange={(e) => setDecalage(Number(e.target.value) || 0)}
            title="Nombre d’entrées à ignorer depuis le début"
          />
        </label>
        <label className="text-xs space-y-1">
          <span className="font-semibold text-slate-600">Gravité</span>
          <input
            className="dsi-input w-36"
            value={niveauGravite}
            onChange={(e) => setNiveauGravite(e.target.value)}
            placeholder="optionnel"
          />
        </label>
        <label className="text-xs space-y-1">
          <span className="font-semibold text-slate-600">Action</span>
          <input
            className="dsi-input w-40"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="optionnel"
          />
        </label>
        <label className="text-xs space-y-1">
          <span className="font-semibold text-slate-600">Type d’élément</span>
          <input
            className="dsi-input w-36"
            value={cibleType}
            onChange={(e) => setCibleType(e.target.value)}
            placeholder="optionnel"
          />
        </label>
        <div className="flex items-end gap-2">
          <button type="button" className="dsi-btn-secondary text-xs" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
          {isSuperAdmin() && (
            <button
              type="button"
              className="dsi-btn-secondary text-xs"
              disabled={busy}
              onClick={() => setConfirmArchive(true)}
            >
              <Archive size={14} /> Archiver
            </button>
          )}
        </div>
      </div>

      <p className="text-[11px] text-slate-500">
        {rows.length} entrée{rows.length !== 1 ? 's' : ''} affichée{rows.length !== 1 ? 's' : ''}
        {decalage > 0 ? ` (à partir de la ${decalage + 1}ᵉ)` : ''}.
      </p>

      {loading ? (
        <div className="dsi-card p-10 text-center text-xs text-slate-500">Chargement du journal…</div>
      ) : (
        <div className="dsi-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Auteur</th>
                  <th className="px-3 py-2">Élément concerné</th>
                  <th className="px-3 py-2">Gravité</th>
                  <th className="px-3 py-2">Conservation</th>
                  <th className="px-3 py-2">Détail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 align-top">
                    <td className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap">
                      {r.created_at || '—'}
                      {r.date_archivage ? (
                        <div className="text-slate-400">Archivé le {r.date_archivage}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs font-semibold">{r.action || '—'}</td>
                    <td className="px-3 py-2 text-[11px] font-mono">{r.auteur_id ?? '—'}</td>
                    <td className="px-3 py-2 text-[11px]">
                      {r.cible_type || '—'}
                      {r.cible_id != null ? (
                        <span className="font-mono text-slate-500"> #{r.cible_id}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-[11px]">{r.niveau_gravite || '—'}</td>
                    <td className="px-3 py-2 text-[11px]">{r.retention || '—'}</td>
                    <td className="px-3 py-2 text-[10px] text-slate-500 max-w-[240px]">
                      <div className="truncate" title={formatJsonField(r.ancienne_valeur)}>
                        Avant : {formatJsonField(r.ancienne_valeur)}
                      </div>
                      <div className="truncate" title={formatJsonField(r.nouvelle_valeur)}>
                        Après : {formatJsonField(r.nouvelle_valeur)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!rows.length && <EmptyState title="Journal vide" message="Aucune entrée pour ces filtres." />}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          className="dsi-btn-secondary text-xs"
          disabled={decalage <= 0 || loading}
          onClick={() => setDecalage(Math.max(0, decalage - limite))}
        >
          Précédent
        </button>
        <button
          type="button"
          className="dsi-btn-secondary text-xs"
          disabled={rows.length < limite || loading}
          onClick={() => setDecalage(decalage + limite)}
        >
          Suivant
        </button>
      </div>

      {confirmArchive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setConfirmArchive(false)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold">Lancer l’archivage du journal ?</h3>
            <p className="text-xs text-slate-600">
              Les anciennes entrées seront archivées selon les règles de conservation. Cette action est réservée aux super-administrateurs.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setConfirmArchive(false)}>
                Annuler
              </button>
              <button type="button" className="dsi-btn-primary text-xs" onClick={runArchive}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
